import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, MessageCircle, Layers, ListTree, History, Plus, Play, Send, Loader2, Trash2, Edit3, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const CHANNELS = [
  { id: "email", label: "Email", icon: Mail },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
];
const FREQS = [
  { id: "", label: "All frequencies" },
  { id: "one_time", label: "One Time" },
  { id: "monthly", label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
  { id: "annually", label: "Annually" },
];

const SUB_TABS = [
  { id: "smtp", label: "SMTP (Email)", icon: Mail },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "templates", label: "Templates", icon: Layers },
  { id: "rules", label: "Rules", icon: ListTree },
  { id: "sent", label: "Sent Log", icon: History },
];

const DEFAULT_TEMPLATE_BODY = `<p>Dear {customer_name},</p>
<p>Our records indicate that the following invoice is currently outstanding:</p>
<ul>
  <li><strong>Project:</strong> {project_name}</li>
  <li><strong>Amount Due:</strong> ₹{amount_due}</li>
  <li><strong>Due Date:</strong> {due_date}</li>
  <li><strong>Days Overdue:</strong> {days_overdue}</li>
</ul>
<p>We would appreciate settlement at the earliest. Please reply if you have any questions.</p>
<p>Warm regards,<br/>Absolin Collections Team</p>`;

export default function RemindersSettings() {
  const [sub, setSub] = useState("smtp");

  return (
    <Card>
      <CardContent className="p-0">
        {/* Sub-tabs */}
        <div className="flex items-center gap-1 border-b border-slate-100 px-3 pt-3 overflow-x-auto" data-testid="reminders-subtabs">
          {SUB_TABS.map(t => {
            const Icon = t.icon;
            const active = sub === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSub(t.id)}
                data-testid={`reminders-subtab-${t.id}`}
                className={`px-3 py-2 text-xs rounded-t-md flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap ${active ? "border-[#0066CC] text-[#0066CC] font-semibold bg-blue-50/50" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-4 sm:p-5">
          {sub === "smtp" && <SmtpPanel />}
          {sub === "whatsapp" && <WhatsAppPanel />}
          {sub === "templates" && <TemplatesPanel />}
          {sub === "rules" && <RulesPanel />}
          {sub === "sent" && <SentLogPanel />}
        </div>
      </CardContent>
    </Card>
  );
}

/* ================================================================
   SMTP Panel
   =============================================================== */
function SmtpPanel() {
  const [form, setForm] = useState({ host: "", port: 587, username: "", password: "", use_tls: true, use_ssl: false, from_email: "", from_name: "Absolin Collections", reply_to: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/collections/reminders/settings");
      if (r.data?.smtp) setForm(prev => ({ ...prev, ...r.data.smtp }));
    } catch { /* ignore */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      // GET current, patch smtp
      const cur = (await api.get("/collections/reminders/settings")).data || {};
      await api.post("/collections/reminders/settings", { ...cur, smtp: form });
      toast.success("SMTP settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error("Enter a recipient email"); return; }
    setSendingTest(true);
    try {
      await api.post("/collections/reminders/test-smtp", { to_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    setSendingTest(false);
  };

  if (loading) return <div className="text-center py-8 text-xs text-slate-400"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="smtp-panel">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">SMTP (Your own email domain)</h3>
        <p className="text-[11px] text-slate-500 mt-0.5">Configure your outgoing mail server. Password is stored securely and never re-displayed.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-[10px]">SMTP Host</Label><Input value={form.host} onChange={e => setForm({ ...form, host: e.target.value })} placeholder="smtp.zoho.com / smtp.office365.com" data-testid="smtp-host" /></div>
        <div><Label className="text-[10px]">Port</Label><Input type="number" value={form.port} onChange={e => setForm({ ...form, port: parseInt(e.target.value, 10) || 0 })} placeholder="587" data-testid="smtp-port" /></div>
        <div><Label className="text-[10px]">Username</Label><Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="ap@absolin.com" data-testid="smtp-user" /></div>
        <div><Label className="text-[10px]">Password</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="App password" data-testid="smtp-pass" /></div>
        <div><Label className="text-[10px]">From Email</Label><Input value={form.from_email} onChange={e => setForm({ ...form, from_email: e.target.value })} placeholder="collections@absolin.com" data-testid="smtp-from-email" /></div>
        <div><Label className="text-[10px]">From Name</Label><Input value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })} data-testid="smtp-from-name" /></div>
        <div><Label className="text-[10px]">Reply-To (optional)</Label><Input value={form.reply_to} onChange={e => setForm({ ...form, reply_to: e.target.value })} placeholder="finance@absolin.com" /></div>
      </div>

      <div className="flex items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5"><Checkbox checked={form.use_tls} onCheckedChange={v => setForm({ ...form, use_tls: !!v, use_ssl: v ? false : form.use_ssl })} data-testid="smtp-use-tls" /> STARTTLS (port 587)</label>
        <label className="flex items-center gap-1.5"><Checkbox checked={form.use_ssl} onCheckedChange={v => setForm({ ...form, use_ssl: !!v, use_tls: v ? false : form.use_tls })} data-testid="smtp-use-ssl" /> Implicit SSL (port 465)</label>
      </div>

      <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-slate-100">
        <Button onClick={save} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1.5" data-testid="smtp-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save SMTP
        </Button>
        <div className="flex items-center gap-1.5 ml-auto">
          <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@example.com" className="w-56 h-9" data-testid="smtp-test-email" />
          <Button variant="outline" size="sm" onClick={sendTest} disabled={sendingTest} className="gap-1" data-testid="smtp-test-btn">
            {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Test
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   WhatsApp Panel
   =============================================================== */
function WhatsAppPanel() {
  const [form, setForm] = useState({ enabled: false, access_token: "", phone_number_id: "", waba_id: "", api_version: "v21.0" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testTpl, setTestTpl] = useState("hello_world");
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/collections/reminders/settings");
      if (r.data?.whatsapp) setForm(prev => ({ ...prev, ...r.data.whatsapp }));
    } catch { /* ignore */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const cur = (await api.get("/collections/reminders/settings")).data || {};
      await api.post("/collections/reminders/settings", { ...cur, whatsapp: form });
      toast.success("WhatsApp settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testPhone) { toast.error("Enter target phone (E.164 without +)"); return; }
    setSendingTest(true);
    try {
      const r = await api.post("/collections/reminders/test-whatsapp", { to_phone: testPhone.replace(/^\+/, ""), template_name: testTpl, lang_code: "en_US" });
      toast.success(`Sent. Meta message id: ${r.data?.meta_response?.messages?.[0]?.id?.slice(0, 20) || "OK"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    setSendingTest(false);
  };

  if (loading) return <div className="text-center py-8 text-xs text-slate-400"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="whatsapp-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">WhatsApp Cloud API (Meta)</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Create a System User in your Meta Business account, generate a <strong>permanent</strong> token, and paste it here. See <a className="text-[#0066CC] underline" href="https://developers.facebook.com/docs/whatsapp/cloud-api" target="_blank" rel="noopener noreferrer">docs</a>.</p>
        </div>
        <label className="flex items-center gap-1.5 text-xs shrink-0"><Checkbox checked={form.enabled} onCheckedChange={v => setForm({ ...form, enabled: !!v })} data-testid="wa-enabled" /> Enabled</label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Label className="text-[10px]">System User Access Token (permanent)</Label><Input type="password" value={form.access_token} onChange={e => setForm({ ...form, access_token: e.target.value })} placeholder="EAAG..." data-testid="wa-token" /></div>
        <div><Label className="text-[10px]">Phone Number ID</Label><Input value={form.phone_number_id} onChange={e => setForm({ ...form, phone_number_id: e.target.value })} placeholder="15551234567890" data-testid="wa-phone-id" /></div>
        <div><Label className="text-[10px]">WABA ID</Label><Input value={form.waba_id} onChange={e => setForm({ ...form, waba_id: e.target.value })} placeholder="1234567890" data-testid="wa-waba" /></div>
        <div><Label className="text-[10px]">Graph API Version</Label><Input value={form.api_version} onChange={e => setForm({ ...form, api_version: e.target.value })} data-testid="wa-version" /></div>
      </div>

      <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-slate-100">
        <Button onClick={save} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1.5" data-testid="wa-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save WhatsApp Config
        </Button>
        <div className="flex items-center gap-1.5 ml-auto">
          <Input value={testTpl} onChange={e => setTestTpl(e.target.value)} placeholder="hello_world" className="w-40 h-9" data-testid="wa-test-template" />
          <Input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="919876543210" className="w-40 h-9" data-testid="wa-test-phone" />
          <Button variant="outline" size="sm" onClick={sendTest} disabled={sendingTest} className="gap-1" data-testid="wa-test-btn">
            {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Test
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   Templates Panel
   =============================================================== */
function TemplatesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/collections/reminders/templates")).data); }
    catch { toast.error("Failed to load templates"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = (channel = "email") => setEditing({
    id: null,
    name: channel === "email" ? "Overdue Reminder — Email" : "Overdue Reminder — WhatsApp",
    channel,
    subject: channel === "email" ? "Payment Reminder — {project_name} · ₹{amount_due} due" : "",
    body_html: channel === "email" ? DEFAULT_TEMPLATE_BODY : "",
    whatsapp_template_name: channel === "whatsapp" ? "payment_reminder" : "",
    whatsapp_lang_code: "en_US",
    variables: ["customer_name", "project_name", "amount_due", "days_overdue", "due_date"],
  });

  const save = async () => {
    try {
      if (editing.id) await api.put(`/collections/reminders/templates/${editing.id}`, editing);
      else await api.post("/collections/reminders/templates", editing);
      toast.success("Template saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template? Rules pointing to it will stop firing.")) return;
    try { await api.delete(`/collections/reminders/templates/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="templates-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Message Templates</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Available variables: <code className="text-[10px] bg-slate-100 px-1 rounded">{`{customer_name} {project_name} {amount_due} {days_overdue} {due_date}`}</code></p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1" onClick={() => openNew("email")} data-testid="tpl-new-email"><Plus className="w-3.5 h-3.5" /> Email Template</Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => openNew("whatsapp")} data-testid="tpl-new-whatsapp"><Plus className="w-3.5 h-3.5" /> WhatsApp Template</Button>
        </div>
      </div>

      {loading ? <div className="text-center py-8"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div> :
        rows.length === 0 ? <p className="text-xs text-slate-400 text-center py-8">No templates yet. Click above to create one.</p> :
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs" data-testid="tpl-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Name</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Channel</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Subject / WA name</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(t => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-2.5 font-medium text-slate-800">{t.name}</td>
                    <td className="p-2.5"><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-[#0066CC]">{t.channel}</span></td>
                    <td className="p-2.5 text-slate-600 max-w-[400px] truncate">{t.channel === "email" ? t.subject : t.whatsapp_template_name}</td>
                    <td className="p-2.5 text-right">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditing(t)} data-testid={`tpl-edit-${t.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(t.id)} data-testid={`tpl-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      }

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Name *</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="tpl-name" /></div>
                <div><Label className="text-xs">Channel</Label>
                  <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={editing.channel} onChange={e => setEditing({ ...editing, channel: e.target.value })} data-testid="tpl-channel">
                    {CHANNELS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              {editing.channel === "email" ? (
                <>
                  <div><Label className="text-xs">Subject *</Label><Input value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })} data-testid="tpl-subject" /></div>
                  <div><Label className="text-xs">Body (HTML)</Label><Textarea rows={10} value={editing.body_html} onChange={e => setEditing({ ...editing, body_html: e.target.value })} className="font-mono text-[11px]" data-testid="tpl-body" /></div>
                </>
              ) : (
                <>
                  <div><Label className="text-xs">WhatsApp Template Name * (exactly as approved in Meta)</Label><Input value={editing.whatsapp_template_name} onChange={e => setEditing({ ...editing, whatsapp_template_name: e.target.value })} placeholder="payment_reminder" data-testid="tpl-wa-name" /></div>
                  <div><Label className="text-xs">Language Code</Label><Input value={editing.whatsapp_lang_code} onChange={e => setEditing({ ...editing, whatsapp_lang_code: e.target.value })} placeholder="en_US" /></div>
                  <div>
                    <Label className="text-xs">Body Variables (in order shown in Meta template body)</Label>
                    <Input value={(editing.variables || []).join(", ")} onChange={e => setEditing({ ...editing, variables: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} placeholder="customer_name, project_name, amount_due" data-testid="tpl-vars" />
                    <p className="text-[10px] text-slate-400 mt-1">These map to {`{{1}} {{2}} ...`} placeholders in your approved template.</p>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button className="bg-[#0066CC]" onClick={save} data-testid="tpl-save-btn">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================================================================
   Rules Panel
   =============================================================== */
function RulesPanel() {
  const [rows, setRows] = useState([]);
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rul, tp] = await Promise.all([
        api.get("/collections/reminders/rules"),
        api.get("/collections/reminders/templates"),
      ]);
      setRows(rul.data);
      setTpls(tp.data);
    } catch { toast.error("Failed to load rules"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ id: null, name: "", days_overdue: 7, channel: "email", template_id: "", active: true, frequency_filter: "", min_outstanding: 0 });

  const save = async () => {
    if (!editing.name || !editing.template_id) { toast.error("Name and template are required"); return; }
    try {
      if (editing.id) await api.put(`/collections/reminders/rules/${editing.id}`, editing);
      else await api.post("/collections/reminders/rules", editing);
      toast.success("Rule saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try { await api.delete(`/collections/reminders/rules/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const runNow = async () => {
    if (!window.confirm("Run reminder scheduler now? This will iterate all active rules and send reminders for currently overdue bills.")) return;
    setRunning(true);
    try {
      const r = await api.post("/collections/reminders/run-scheduler");
      toast.success(`Scheduler done: ${r.data.sent} sent, ${r.data.skipped} skipped, ${r.data.failures} failures`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    setRunning(false);
  };

  const tplName = (id) => tpls.find(t => t.id === id)?.name || <span className="text-red-500 italic">deleted</span>;

  return (
    <div className="space-y-4" data-testid="rules-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Reminder Rules</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Every rule fires once per (project, billing line) per 24h window when the bill is overdue by the specified number of days.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-1" onClick={runNow} disabled={running} data-testid="rules-run-now">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run Scheduler Now
          </Button>
          <Button size="sm" className="bg-[#0066CC] gap-1" onClick={openNew} data-testid="rules-new-btn"><Plus className="w-3.5 h-3.5" /> New Rule</Button>
        </div>
      </div>

      {loading ? <div className="text-center py-8"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div> :
        rows.length === 0 ? <p className="text-xs text-slate-400 text-center py-8">No rules configured. Add one above.</p> :
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs" data-testid="rules-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Name</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600">≥ Days Overdue</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Channel</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Template</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Freq Filter</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600">Min Due</th>
                  <th className="text-center p-2.5 font-semibold text-slate-600">Active</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-2.5 font-medium text-slate-800">{r.name}</td>
                    <td className="p-2.5 text-right font-mono">{r.days_overdue}</td>
                    <td className="p-2.5"><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-[#0066CC]">{r.channel}</span></td>
                    <td className="p-2.5 text-slate-600">{tplName(r.template_id)}</td>
                    <td className="p-2.5 text-slate-600">{FREQS.find(f => f.id === (r.frequency_filter || ""))?.label || "All"}</td>
                    <td className="p-2.5 text-right text-slate-700 tabular-nums">₹{(r.min_outstanding || 0).toLocaleString("en-IN")}</td>
                    <td className="p-2.5 text-center">{r.active ? "✅" : "⏸"}</td>
                    <td className="p-2.5 text-right">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditing(r)} data-testid={`rule-edit-${r.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(r.id)} data-testid={`rule-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      }

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Rule" : "New Rule"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label className="text-xs">Name *</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="7-day reminder — email" data-testid="rule-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">≥ Days Overdue</Label><Input type="number" value={editing.days_overdue} onChange={e => setEditing({ ...editing, days_overdue: parseInt(e.target.value, 10) || 0 })} data-testid="rule-days" /></div>
                <div><Label className="text-xs">Channel</Label>
                  <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={editing.channel} onChange={e => setEditing({ ...editing, channel: e.target.value, template_id: "" })} data-testid="rule-channel">
                    {CHANNELS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div><Label className="text-xs">Template *</Label>
                <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={editing.template_id} onChange={e => setEditing({ ...editing, template_id: e.target.value })} data-testid="rule-template">
                  <option value="">— select —</option>
                  {tpls.filter(t => t.channel === editing.channel).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {tpls.filter(t => t.channel === editing.channel).length === 0 && <p className="text-[10px] text-amber-600 mt-1">No {editing.channel} templates found. Create one first.</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Frequency Filter (optional)</Label>
                  <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={editing.frequency_filter || ""} onChange={e => setEditing({ ...editing, frequency_filter: e.target.value })}>
                    {FREQS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </div>
                <div><Label className="text-xs">Min Outstanding (₹)</Label><Input type="number" value={editing.min_outstanding} onChange={e => setEditing({ ...editing, min_outstanding: parseInt(e.target.value, 10) || 0 })} data-testid="rule-min" /></div>
              </div>
              <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={editing.active} onCheckedChange={v => setEditing({ ...editing, active: !!v })} data-testid="rule-active" /> Active</label>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button className="bg-[#0066CC]" onClick={save} data-testid="rule-save-btn">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================================================================
   Sent Log Panel
   =============================================================== */
function SentLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/collections/reminders/sent")).data); }
    catch { toast.error("Failed to load log"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="sent-log-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Sent Log</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Last 200 reminders dispatched (manual + scheduler).</p>
        </div>
        <Button size="sm" variant="outline" className="gap-1" onClick={load}><RefreshCw className="w-3.5 h-3.5" /> Refresh</Button>
      </div>
      {loading ? <div className="text-center py-8"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div> :
        rows.length === 0 ? <p className="text-xs text-slate-400 text-center py-8">No reminders sent yet.</p> :
          <div className="overflow-x-auto rounded-lg border border-slate-200 max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="sent-log-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left p-2.5 font-semibold text-slate-600">When (UTC)</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Project</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Channel</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">To</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600">₹ Due</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600">Days</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Trigger</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-2.5 text-slate-600 whitespace-nowrap">{r.sent_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="p-2.5 font-medium text-slate-800 max-w-[220px] truncate">{r.project_name}</td>
                    <td className="p-2.5"><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-[#0066CC]">{r.channel}</span></td>
                    <td className="p-2.5 text-slate-600 max-w-[220px] truncate">{r.to || "—"}</td>
                    <td className="p-2.5 text-right font-mono">₹{Number(r.amount_due || 0).toLocaleString("en-IN")}</td>
                    <td className="p-2.5 text-right text-slate-500">{r.days_overdue}</td>
                    <td className="p-2.5 text-slate-500 text-[10px]">{r.trigger}</td>
                    <td className="p-2.5">
                      {r.status === "sent" ?
                        <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">sent</span> :
                        <span className="text-[10px] font-semibold bg-red-100 text-red-700 px-1.5 py-0.5 rounded" title={r.error}>failed</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      }
    </div>
  );
}
